import rootStore from '@vue-storefront/core/store'
import { magentoRestBaseUrl } from '@vue-storefront/core/helpers/magentoRestUrl'
import Task from '@vue-storefront/core/lib/sync/types/Task'
import CartItem from '@vue-storefront/core/modules/cart/types/CartItem'

/** Magento rule: use numeric customer cart id when logged in, otherwise guest quote id (mask). */
function isNumericCartId (val: string | null | undefined): boolean {
  if (val == null || val === '') return false
  return Number(parseFloat(String(val))).toString() === String(val)
}

function asTask (result: any, resultCode: number): Task {
  return {
    result,
    resultCode,
    code: resultCode,
    transmited: true,
    acknowledged: false,
    silent: true,
    task_id: 0,
    transmited_at: new Date(),
    url: '',
    payload: {},
    meta: null
  } as Task
}

function restCartResourcePathFor (
  cartId: string,
  minePath: string,
  guestPath: (id: string) => string
): string {
  const userToken = rootStore.getters['user/getUserToken']
  if (userToken && isNumericCartId(cartId)) {
    return minePath
  }
  return guestPath(cartId)
}

function requireGuestCartId (cartId: string): Task | null {
  if (cartId) return null
  return asTask({ message: 'Cart id is required for guest cart requests.' }, 400)
}

async function restApiRequest (
  resourcePath: string,
  init: { method: string; headers?: Record<string, string>; body?: string; mode?: string }
): Promise<Task> {
  const base = magentoRestBaseUrl()
  if (!base) {
    return asTask({
      message: 'Set magentoApi.endpoint (absolute or path like /rest/all/V1), or magento2.api.url. For a path endpoint, set magentoApi.proxyTarget on the server to forward /rest to Magento.'
    }, 500)
  }
  const url = `${base}/V1${resourcePath}`
  const userToken = rootStore.getters['user/getUserToken']
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string>),
    ...(userToken ? { Authorization: `Bearer ${userToken}` } : {})
  }
  const fetchOpts: Record<string, any> = { method: init.method, mode: init.mode || 'cors', headers }
  if (init.body !== undefined) {
    fetchOpts.body = init.body
  }
  const res = await fetch(url, fetchOpts)
  const text = await res.text()
  let body: any = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = text
    }
  }
  if (res.ok) {
    const payload = text === '' || body === '' ? true : body
    return asTask(payload, 200)
  }
  return asTask(body || { message: res.statusText }, res.status)
}

export async function restApiCreateCart (guestCart = false): Promise<Task> {
  const userToken = rootStore.getters['user/getUserToken']
  if (userToken && !guestCart) {
    return restApiRequest('/carts/mine', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      mode: 'cors',
      body: '{}'
    })
  }
  return restApiRequest('/guest-carts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    mode: 'cors',
    body: '{}'
  })
}

export async function restApiGetCartItems (): Promise<Task> {
  const cartId = rootStore.getters['cart/getCartToken']
  const userToken = rootStore.getters['user/getUserToken']
  if (!userToken) {
    const err = requireGuestCartId(cartId)
    if (err) return err
  }
  const path = restCartResourcePathFor(
    cartId,
    '/carts/mine/items',
    (id) => `/guest-carts/${id}/items/`
  )
  return restApiRequest(path, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    mode: 'cors'
  })
}

export async function restApiGetTotals (): Promise<Task> {
  const cartId = rootStore.getters['cart/getCartToken']
  const userToken = rootStore.getters['user/getUserToken']
  if (!userToken) {
    const err = requireGuestCartId(cartId)
    if (err) return err
  }
  const path = restCartResourcePathFor(
    cartId,
    '/carts/mine/totals',
    (id) => `/guest-carts/${id}/totals/`
  )
  return restApiRequest(path, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    mode: 'cors'
  })
}

export async function restApiGetPaymentMethods (): Promise<Task> {
  const cartId = rootStore.getters['cart/getCartToken']
  const userToken = rootStore.getters['user/getUserToken']
  if (!userToken) {
    const err = requireGuestCartId(cartId)
    if (err) return err
  }
  const path = restCartResourcePathFor(
    cartId,
    '/carts/mine/payment-methods',
    (id) => `/guest-carts/${id}/payment-methods`
  )
  return restApiRequest(path, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    mode: 'cors'
  })
}

export async function restApiPostShippingInformation (bodyRoot: { addressInformation: any }): Promise<Task> {
  const cartId = rootStore.getters['cart/getCartToken']
  const userToken = rootStore.getters['user/getUserToken']
  if (!userToken) {
    const err = requireGuestCartId(cartId)
    if (err) return err
  }
  const path = restCartResourcePathFor(
    cartId,
    '/carts/mine/shipping-information',
    (id) => `/guest-carts/${id}/shipping-information`
  )
  return restApiRequest(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    mode: 'cors',
    body: JSON.stringify(bodyRoot)
  })
}

export async function restApiEstimateShippingMethods (address: any): Promise<Task> {
  const cartId = rootStore.getters['cart/getCartToken']
  const userToken = rootStore.getters['user/getUserToken']
  if (!userToken) {
    const err = requireGuestCartId(cartId)
    if (err) return err
  }
  const path = restCartResourcePathFor(
    cartId,
    '/carts/mine/estimate-shipping-methods',
    (id) => `/guest-carts/${id}/estimate-shipping-methods`
  )
  return restApiRequest(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    mode: 'cors',
    body: JSON.stringify({ address })
  })
}

export async function restApiUpdateCartItem (cartServerToken: string, cartItem: CartItem): Promise<Task> {
  const userToken = rootStore.getters['user/getUserToken']
  if (!userToken) {
    const err = requireGuestCartId(cartServerToken)
    if (err) return err
  }
  const path = restCartResourcePathFor(
    cartServerToken,
    '/carts/mine/items',
    (id) => `/guest-carts/${id}/items`
  )
  return restApiRequest(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    mode: 'cors',
    body: JSON.stringify({
      cartItem: {
        ...cartItem,
        quoteId: cartItem.quoteId || cartServerToken
      }
    })
  })
}

export async function restApiDeleteCartItem (cartServerToken: string, cartItem: CartItem): Promise<Task> {
  const userToken = rootStore.getters['user/getUserToken']
  if (!userToken) {
    const err = requireGuestCartId(cartServerToken)
    if (err) return err
  }
  const path = restCartResourcePathFor(
    cartServerToken,
    `/carts/mine/items/${cartItem.item_id}`,
    (id) => `/guest-carts/${id}/items/${cartItem.item_id}`
  )
  return restApiRequest(path, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    mode: 'cors'
  })
}

export async function restApiApplyCoupon (couponCode: string): Promise<Task> {
  const cartId = rootStore.getters['cart/getCartToken']
  const userToken = rootStore.getters['user/getUserToken']
  if (!userToken) {
    const err = requireGuestCartId(cartId)
    if (err) return err
  }
  const enc = encodeURIComponent(couponCode)
  const path = restCartResourcePathFor(
    cartId,
    `/carts/mine/coupons/${enc}`,
    (id) => `/guest-carts/${id}/coupons/${enc}`
  )
  return restApiRequest(path, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    mode: 'cors'
  })
}

export async function restApiRemoveCoupon (): Promise<Task> {
  const cartId = rootStore.getters['cart/getCartToken']
  const userToken = rootStore.getters['user/getUserToken']
  if (!userToken) {
    const err = requireGuestCartId(cartId)
    if (err) return err
  }
  const path = restCartResourcePathFor(
    cartId,
    '/carts/mine/coupons',
    (id) => `/guest-carts/${id}/coupons`
  )
  return restApiRequest(path, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    mode: 'cors'
  })
}
